import { useState } from 'react'

import { Text } from '@yes.technology/react-toolkit'
import { GridContainer } from 'shared/style/grid'
import useStyleMaps from 'site/Renderer/hooks/useStyleMap'
import { useSiteState } from 'siteState/shared'
import AppliedFilters, {
  AppliedFilterFieldValuesProps
} from '../AppliedFilters/AppliedFilters'
import { ChartProps, CockpitProps } from './Cockpit.types'
import ChartWrapper from './components/ChartWrapper'
import Sections from './components/Sections'
import { useClassifiedCharts } from './hooks'
import getSectionTitle from './utils/getSectionTitle'

export const getRows = (charts: ChartProps[]) => {
  const rows = []
  let currentRow: ChartProps[] = []
  let currentSum = 0

  charts.forEach((chart) => {
    const column = Number(chart?.column?.match(/\d+/)?.[0] ?? 12)

    if (currentSum + column > 12) {
      rows.push(currentRow)
      currentRow = []
      currentSum = 0
    }
    currentRow.push(chart)
    currentSum += column
  })

  if (currentRow.length > 0) {
    rows.push(currentRow)
  }

  return rows
}

const Cockpit = ({ props = { data: [], titles: [] } }: CockpitProps) => {
  const [section, setSection] = useState(0)

  const {
    data = [],
    titles = [],
    applied_filters = [],
    uuid_classification = '',
    styleMap = {}
  } = props

  const [dynamicAppliedFilters] = useSiteState<AppliedFilterFieldValuesProps[]>(
    'cockpit-applied-filters'
  )

  const getAppliedFilters = (): AppliedFilterFieldValuesProps[] => {
    try {
      if (dynamicAppliedFilters !== undefined) {
        return dynamicAppliedFilters
      }
      if (applied_filters?.length > 0) {
        return JSON.parse(applied_filters[0].des)
      }
      return []
    } catch (error) {
      console.error('Error parsing JSON:', error)
      return []
    }
  }

  const appliedFilters: AppliedFilterFieldValuesProps[] = getAppliedFilters()

  const styleProps = useStyleMaps(styleMap)

  const handleNavigateSections = (index: number) => {
    setSection(index - 1)
  }

  const { classifiedCharts, sectionsUuids, isLoading } = useClassifiedCharts({
    charts: data,
    classificationUuid: uuid_classification
  })

  const sectionTitle = getSectionTitle({
    sectionUuid: sectionsUuids[section],
    titles
  })

  if (isLoading) return null

  const rows = getRows(classifiedCharts[section] as ChartProps[])

  return (
    <Sections
      onNavigate={handleNavigateSections}
      currentSection={section + 1}
      totalSections={classifiedCharts.length}
      style={styleProps}
      title={sectionTitle}
    >
      {sectionTitle && (
        <div className='d-flex justify-content-center mt-3'>
          <Text variant='heading-emphasis-01' as='h1'>
            {sectionTitle}
          </Text>
        </div>
      )}
      <GridContainer>
        {rows.map((row, rowIndex) => (
          <div className={`row${rowIndex === 0 ? ' mt-3' : ''}`} key={rowIndex}>
            {row.map(
              (chart) =>
                chart && (
                  <ChartWrapper
                    key={chart.uuid}
                    dataset={chart.dataset}
                    des={chart.des}
                    template={chart.template}
                    uuid={chart.uuid}
                    column={chart.column}
                  />
                )
            )}
          </div>
        ))}
        <div className='row mb-3'>
          <div className='col-md-12'>
            <AppliedFilters fieldValues={appliedFilters} />
          </div>
        </div>
      </GridContainer>
    </Sections>
  )
}

export default Cockpit
